import React, { useEffect, useState } from "react";
import { Card, CardBody, Row, Col, Input, Button, Table, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import Pagination from "../../Helpers/Pagination";
import { toast } from "react-toastify";
import Select from "react-select";
import "boxicons/css/boxicons.min.css";

const SubCategoryList = () => {

    const dispatch = useDispatch();

    const [rows, setRows] = useState([]);
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const limit = 10;

    /* CATEGORY DROPDOWN */

    const loading = useSelector(state => state.loading.loading);
    const [categories, setCategories] = useState([]);
    const [filterCategory, setFilterCategory] = useState(null);

    /* MODAL */
    const [open, setOpen] = useState(false);
    const [name, setName] = useState("");
    const [category, setCategory] = useState(null);
    const [editId, setEditId] = useState(null);

    /* LOAD CATEGORIES */
    const loadCategories = async () => {
        const res = await Handler({ method: "get", url: "/categories/list.php?limit=1000" });
        if (res?.success) {
            setCategories((res.data?.categories || []).map(c => ({ value: c.id, label: c.name })));
        }
    };

    /* LOAD SUB CATEGORIES */
    const load = async (p = page, q = search) => {
        dispatch(loadingTrue());

        const catParam = filterCategory?.value ? `&category_id=${filterCategory.value}` : "";

        const res = await Handler({
            method: "get",
            url: `/sub_categories/list.php?page=${p}&limit=${limit}&search=${q}${catParam}`
        });

        if (res?.success) {
            setRows(res.data?.sub_categories || []);
            setTotal(res.data?.pagination?.total || 0);
        } else toast.error(res?.message || "Failed to load sub-categories");

        dispatch(loadingFalse());
    };

    useEffect(() => { loadCategories(); }, []);
    useEffect(() => { load(1, search); }, [search, filterCategory]);
    useEffect(() => { load(page, search); }, [page]);

    /* OPEN ADD */
    const openAdd = () => {
        setEditId(null);
        setName("");
        setCategory(null);
        setOpen(true);
    };

    /* OPEN EDIT */
    const openEdit = (row) => {
        setEditId(row.id);
        setName(row.name);
        setCategory({ value: row.category_id, label: row.category_name });
        setOpen(true);
    };

    /* SAVE (ADD / EDIT) */
    const save = async () => {
        if (!category) return toast.error("Category required");
        if (!name.trim()) return toast.error("Sub-category name required");

        dispatch(loadingTrue());

        const res = editId
            ? await Handler({
                method: "patch",
                url: "/sub_categories/update.php",
                data: { id: editId, name }
            })
            : await Handler({
                method: "post",
                url: "/sub_categories/create.php",
                data: { category_id: category.value, name }
            });

        if (res?.success) {
            toast.success(editId ? "Sub-category updated" : "Sub-category added");
            setOpen(false);
            setEditId(null);
            setName("");
            setCategory(null);
            load(1, "");
        } else toast.error(res?.message || "Save failed");

        dispatch(loadingFalse());
    };

    return (
        <>
            {/* HEADER */}
            <Row className="mb-3 align-items-center">
                <Col md={6}>
                    <h5 className="fw-bold mb-0">Sub Categories</h5>
                    <small className="text-muted">Manage product sub-categories</small>
                </Col>
                <Col md={6} className="text-end">
                    <Button disabled={loading} className="list-add-btn d-inline-flex align-items-center gap-1" onClick={openAdd}>
                        <i className="bx bx-plus"></i> Add Sub Category
                    </Button>
                </Col>
            </Row>

            {/* FILTER + SEARCH */}
            <Card className="mb-3">
                <CardBody>
                    <Row>
                        <Col md={4}>
                            <Select
                                options={categories}
                                value={filterCategory}
                                placeholder="Filter by category"
                                onChange={v => { setFilterCategory(v); setPage(1); }}
                                isClearable
                            />
                        </Col>
                        <Col md={8}>
                            <Input
                                placeholder="Search sub-category..."
                                value={search}
                                onChange={e => { setSearch(e.target.value); setPage(1); }}
                            />
                        </Col>
                    </Row>
                </CardBody>
            </Card>

            {/* TABLE */}
            <Card>
                <CardBody>
                    <Table responsive borderless className="modern-table">
                        <thead>
                            <tr>
                                <th>S.No</th>
                                <th>Sub Category</th>
                                <th>Category</th>
                                <th className="text-end">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.length ? rows.map((s, i) => (
                                <tr key={i}>
                                    <td>{i + 1}</td>
                                    <td className="fw-semibold">{s.name}</td>
                                    <td>{s.category_name}</td>
                                    <td className="text-end">
                                        <Button disabled={loading} size="sm" color="light" onClick={() => openEdit(s)}>
                                            <i className="bx bx-edit"></i>
                                        </Button>
                                    </td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan="3" className="text-center text-muted">No sub-categories found</td>
                                </tr>
                            )}
                        </tbody>
                    </Table>

                    <Pagination
                        page={page}
                        total={total}
                        limit={limit}
                        onPrev={() => setPage(p => Math.max(1, p - 1))}
                        onNext={() => setPage(p => p + 1)}
                    />
                </CardBody>
            </Card>

            {/* ADD / EDIT MODAL */}
            <Modal isOpen={open} toggle={() => setOpen(false)} centered>
                <ModalHeader toggle={() => setOpen(false)}>
                    {editId ? "Edit Sub Category" : "Add Sub Category"}
                </ModalHeader>
                <ModalBody>
                    <label className="form-label">Category *</label>
                    <Select
                        options={categories}
                        value={category}
                        placeholder="Select category"
                        onChange={setCategory}
                        isDisabled={!!editId}
                    />

                    <label className="form-label mt-3">Sub Category Name *</label>
                    <Input
                        value={name}
                        placeholder="Enter sub-category name"
                        onChange={e => setName(e.target.value)}
                    />
                </ModalBody>
                <ModalFooter>
                    <Button disabled={loading} color="light" onClick={() => setOpen(false)}>Cancel</Button>
                    <Button disabled={loading} color="success" onClick={save}>
                        <i className="bx bx-check"></i> Save
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    );
};

export default SubCategoryList;
